import { Box, Button, FormHelperText, Typography } from '@mui/material';
import { JSX, PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    canvasPoint,
    midpoint,
    Point,
    toBarePngBase64,
    toPngDataUrl,
} from '../../library/signatureHelper';
import {
    actionsStyles,
    baselineStyles,
    canvasStyles,
    helperTextStyles,
    hintStyles,
    labelStyles,
    padBoxStyles,
    padBoxErrorStyles,
    placeholderStyles,
    wrapperStyles,
} from './signaturePad.styles';

interface ISignaturePadProps {
    /** The drawn signature as bare base64 of a PNG; empty when nothing has been signed. */
    value: string;
    onChange: (value: string) => void;
    label: string;
    hint: string;
    clearLabel: string;
    placeholder: string;
    error?: boolean;
    helperText?: string;
}

/**
 * A box somebody signs in with a finger, a stylus or a mouse.
 *
 * <p>Hand-rolled rather than a signature library, for the same reasons `imageCropHelper`
 * has no cropper behind it: TypeScript is pinned to ^4.9.5 by CRA 5 and Jest 27 predates
 * the `exports` field, so a dependency here would likely need a sixth `moduleNameMapper`
 * entry and a toolchain upgrade. What it replaces is about a hundred lines of pointer
 * handling.
 *
 * <p>Three things about it are load-bearing and easy to undo:
 *
 * <ul>
 *   <li><b>Every canvas call is guarded.</b> jsdom has no canvas backend: `getContext`
 *       returns null and `toDataURL` throws. Unguarded, this component would take the
 *       whole evaluation wizard's test suite down with it the moment it rendered.</li>
 *   <li><b>Drawing happens in the pointer handler, never in `requestAnimationFrame`.</b>
 *       A frame of latency between a finger and its ink reads as a broken control, and
 *       there is nothing to batch -- one stroke segment is one short path.</li>
 *   <li><b>The size is taken on mount and on resize, not from a `ResizeObserver`.</b> The
 *       test setup stubs that to a no-op, so an observer-driven pad would be permanently
 *       zero-sized under test.</li>
 * </ul>
 *
 * <p>Redrawing `value` on mount is what makes the wizard's back button safe: only the
 * active step is mounted, so returning to this one builds a fresh canvas while the form
 * still holds the signature. Without it the drawing disappears and the form goes on
 * claiming the patient signed.
 */
export const SignaturePad = ({
    value,
    onChange,
    label,
    hint,
    clearLabel,
    placeholder,
    error,
    helperText,
}: ISignaturePadProps): JSX.Element => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef<boolean>(false);
    const lastPointRef = useRef<Point | null>(null);
    const lastMidRef = useRef<Point | null>(null);

    /** Read by the resize handler, which must not be rebuilt every time the value changes. */
    const valueRef = useRef<string>(value);
    valueRef.current = value;

    const [hasInk, setHasInk] = useState<boolean>(value !== '');

    /**
     * The backing store is a fixed multiple of the CSS box rather than `devicePixelRatio`,
     * so a phone and a laptop send the same number of bytes and the API's size cap means
     * the same thing everywhere.
     */
    const EXPORT_SCALE = 2;
    const STROKE_WIDTH = 2.2;

    /** Near-black ink, deliberately not a brand token: this prints on a white page, and a
     * clinic's colour would put a green signature on a legal document. */
    const INK = '#1A1A2E';

    const prepare = useCallback((context: CanvasRenderingContext2D): void => {
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.strokeStyle = INK;
        context.lineWidth = STROKE_WIDTH * EXPORT_SCALE;
    }, []);

    /**
     * Sizes the backing store and puts back whatever was already signed.
     *
     * Setting `width` clears the canvas and resets the context, which is why the stroke
     * settings are re-applied here and nowhere else.
     */
    const measure = useCallback((): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;

        if (!canvas) {
            return;
        }

        const box: DOMRect = canvas.getBoundingClientRect();
        const width: number = Math.round(box.width * EXPORT_SCALE);
        const height: number = Math.round(box.height * EXPORT_SCALE);

        // Zero under jsdom, and while the step is still laying out. Sizing to it would
        // throw away the signature and give nothing back.
        if (width === 0 || height === 0) {
            return;
        }

        if (canvas.width === width && canvas.height === height) {
            return;
        }

        canvas.width = width;
        canvas.height = height;

        const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

        if (!context) {
            return;
        }

        prepare(context);

        if (valueRef.current === '') {
            return;
        }

        // The export covers the whole surface, so this is a straight redraw at whatever
        // size the pad now is -- no offset to remember and nothing to line up.
        const image: HTMLImageElement = new Image();

        image.onload = (): void => context.drawImage(image, 0, 0, width, height);
        image.src = toPngDataUrl(valueRef.current);
    }, [prepare]);

    useLayoutEffect(() => {
        measure();
    }, [measure]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        window.addEventListener('orientationchange', measure);

        return (): void => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('orientationchange', measure);
        };
    }, [measure]);

    const pointFrom = (event: PointerEvent<HTMLCanvasElement>, canvas: HTMLCanvasElement): Point =>
        canvasPoint(
            { x: event.clientX, y: event.clientY },
            canvas.getBoundingClientRect(),
            { width: canvas.width, height: canvas.height }
        );

    const handlePointerDown = (event: PointerEvent<HTMLCanvasElement>): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;
        const context: CanvasRenderingContext2D | null = canvas?.getContext('2d') ?? null;

        if (!canvas || !context) {
            return;
        }

        // Capture, so a stroke that leaves the box keeps drawing and still ends here
        // rather than being abandoned mid-line.
        canvas.setPointerCapture(event.pointerId);

        const point: Point = pointFrom(event, canvas);

        drawingRef.current = true;
        lastPointRef.current = point;
        lastMidRef.current = point;

        // A dot, so signing with a full stop leaves something behind.
        context.beginPath();
        context.moveTo(point.x, point.y);
        context.lineTo(point.x, point.y);
        context.stroke();

        setHasInk(true);
    };

    const handlePointerMove = (event: PointerEvent<HTMLCanvasElement>): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;
        const context: CanvasRenderingContext2D | null = canvas?.getContext('2d') ?? null;
        const last: Point | null = lastPointRef.current;
        const lastMid: Point | null = lastMidRef.current;

        if (!drawingRef.current || !canvas || !context || !last || !lastMid) {
            return;
        }

        const point: Point = pointFrom(event, canvas);
        const mid: Point = midpoint(last, point);

        context.beginPath();
        context.moveTo(lastMid.x, lastMid.y);
        context.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
        context.stroke();

        lastPointRef.current = point;
        lastMidRef.current = mid;
    };

    const handlePointerUp = (event: PointerEvent<HTMLCanvasElement>): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;
        const context: CanvasRenderingContext2D | null = canvas?.getContext('2d') ?? null;

        if (!drawingRef.current || !canvas) {
            return;
        }

        // Curving between midpoints leaves the stroke half a segment short of where the
        // pointer actually stopped, because the last midpoint is behind the last point.
        // Unclosed, every stroke ends slightly before the finger lifted -- visible as a
        // signature whose lines do not quite meet.
        if (context && lastMidRef.current && lastPointRef.current) {
            context.beginPath();
            context.moveTo(lastMidRef.current.x, lastMidRef.current.y);
            context.lineTo(lastPointRef.current.x, lastPointRef.current.y);
            context.stroke();
        }

        drawingRef.current = false;
        lastPointRef.current = null;
        lastMidRef.current = null;

        if (canvas.hasPointerCapture(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
        }

        try {
            onChange(toBarePngBase64(canvas.toDataURL('image/png')));
        } catch {
            // jsdom has no toDataURL. There is nothing to export and nothing to report:
            // the form keeps whatever it had.
        }
    };

    const handleClear = (): void => {
        const canvas: HTMLCanvasElement | null = canvasRef.current;
        const context: CanvasRenderingContext2D | null = canvas?.getContext('2d') ?? null;

        if (canvas && context) {
            context.clearRect(0, 0, canvas.width, canvas.height);
            prepare(context);
        }

        setHasInk(false);
        onChange('');
    };

    return (
        <Box sx={wrapperStyles}>
            <Typography sx={labelStyles}>{label}</Typography>
            <Typography sx={hintStyles}>{hint}</Typography>

            <Box sx={error ? padBoxErrorStyles : padBoxStyles}>
                <Box
                    component="canvas"
                    ref={canvasRef}
                    sx={canvasStyles}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                />

                {!hasInk && (
                    <>
                        <Box sx={baselineStyles} />
                        <Typography sx={placeholderStyles}>{placeholder}</Typography>
                    </>
                )}
            </Box>

            <Box sx={actionsStyles}>
                <Button size="small" onClick={handleClear} disabled={!hasInk}>
                    {clearLabel}
                </Button>
            </Box>

            {error && helperText && (
                <FormHelperText error sx={helperTextStyles}>
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};
